/* Creme Castle — delivery slot engine (Step 2, Sept 2026).
 *
 * Pure functions, no DOM. The same file runs on the product page (to draw the
 * picker), in the cart (to re-check every cake before checkout) and in Node
 * (tests). Rules and cities come from Shopify metaobjects, injected as
 * window.ccSlotData by snippets/cc-slot-data.liquid — nothing here is hard-coded.
 *
 * Spec: "Slot Rulebook" (locked 3 Sep 2026).
 *   Family 1, turnaround (instant / quick / designer):
 *     need = rule.min_hours + city.travel_buffer_hours
 *     on the day's LAST window: need = min_hours - squeeze_hours + travel_buffer
 *     offer window W today if W.start >= now + need
 *     designer never gets a window starting before city.designer_first_slot_hour
 *     city.same_day_allowed === false -> no today windows at all
 *     tomorrow and later: every window on the grid
 *   Family 2, cutoff (classic_overnight / designer_overnight):
 *     never same-day; delivery day = now.hour < cutoff ? tomorrow : day after
 *     every window on the delivery day and on any later day
 *   A "pre-order from" date pushes the earliest day out further; never earlier.
 *   Flavour rule (4 Sep 2026): on custom cakes (SKU C*), a flavour outside
 *     rule.transit_safe_flavors skips the FIRST same-day window (opts.skipFirstToday).
 *     Only today is affected; if the first offered slot is tomorrow, every flavour is fine.
 *   Clock: always Asia/Kolkata, never the visitor's zone.
 *   Closed dates (2 Nov 2026, first use Diwali 8 Nov): city.closed_dates lists
 *     YYYY-MM-DD days with no delivery at all, from the delivery_closure
 *     metaobject. A closed day offers no windows, is never the earliest day,
 *     never shows as Today / Tomorrow, and the next open day takes its place.
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.CCSlotEngine = factory();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var IST_OFFSET_MIN = 330; // +05:30, no DST

  /* ---------- IST clock ---------- */

  // Break an absolute instant (ms since epoch) into IST wall-clock parts.
  function istParts(epochMs) {
    var d = new Date(epochMs + IST_OFFSET_MIN * 60000);
    return {
      y: d.getUTCFullYear(),
      m: d.getUTCMonth() + 1,
      d: d.getUTCDate(),
      hour: d.getUTCHours(),
      minute: d.getUTCMinutes(),
      hourDec: d.getUTCHours() + d.getUTCMinutes() / 60,
      ymd: ymdOf(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()),
      dow: d.getUTCDay()
    };
  }
  function pad(n) { return (n < 10 ? "0" : "") + n; }
  function ymdOf(y, m, d) { return y + "-" + pad(m) + "-" + pad(d); }
  // Add whole days to a YYYY-MM-DD string (calendar days, IST-safe because we
  // never touch local time here).
  function addDays(ymd, n) {
    var p = ymd.split("-").map(Number);
    var t = Date.UTC(p[0], p[1] - 1, p[2] + n, 12);
    var d = new Date(t);
    return ymdOf(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
  }
  function daysBetween(a, b) { // b - a in days
    var pa = a.split("-").map(Number), pb = b.split("-").map(Number);
    return Math.round((Date.UTC(pb[0], pb[1] - 1, pb[2]) - Date.UTC(pa[0], pa[1] - 1, pa[2])) / 86400000);
  }
  var DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  var DOW3 = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  var MON3 = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  function dowOf(ymd) { var p = ymd.split("-").map(Number); return new Date(Date.UTC(p[0], p[1] - 1, p[2], 12)).getUTCDay(); }
  function labelLong(ymd) { var p = ymd.split("-").map(Number); return DOW[dowOf(ymd)] + ", " + p[2] + " " + MON3[p[1] - 1] + " " + p[0]; }
  function labelShort(ymd) { var p = ymd.split("-").map(Number); return DOW3[dowOf(ymd)] + ", " + p[2] + " " + MON3[p[1] - 1]; }

  /* ---------- normalisation ---------- */

  function num(v, dflt) { var n = parseFloat(v); return isNaN(n) ? dflt : n; }
  function bool(v) { return v === true || v === "true" || v === 1 || v === "1"; }

  // Rule as stored in the delivery_rule metaobject (all values arrive as strings).
  function normRule(r) {
    if (!r) return null;
    if (r.__norm) return r;
    return {
      __norm: true,
      handle: r.handle,
      family: r.family === "cutoff" ? "cutoff" : "turnaround",
      min_hours: num(r.min_hours, 4),
      squeeze_hours: num(r.squeeze_hours, 0),
      min_days: num(r.min_days, 0),
      cutoff_hour: num(r.cutoff_hour, 19),
      label: r.label || r.handle,
      transit_safe_flavors: flavorList(r.transit_safe_flavors),
      skip_first_slot: bool(r.skip_first_slot_for_other_flavors)
    };
  }
  // Metaobject list field arrives as an array, a JSON string, or a comma string.
  function flavorList(v) {
    if (!v) return [];
    if (typeof v === "string") { try { var j = JSON.parse(v); if (Array.isArray(j)) v = j; else v = v.split(","); } catch (e) { v = v.split(","); } }
    return (v || []).map(function (x) { return String(x).trim().toLowerCase(); }).filter(Boolean);
  }
  // Is this flavour allowed on the first same-day window? No list = everything allowed.
  function isSafeFlavor(rule, flavor) {
    rule = normRule(rule);
    if (!rule || !rule.transit_safe_flavors.length || !flavor) return true;
    return rule.transit_safe_flavors.indexOf(String(flavor).trim().toLowerCase()) !== -1;
  }
  // Does the flavour rule bite for this product + flavour choice?
  function flavorRuleActive(rule, ctx) {
    rule = normRule(rule); ctx = ctx || {};
    if (!rule || !rule.skip_first_slot || !ctx.customCake) return false;
    var fl = [].concat(ctx.flavors || (ctx.flavor ? [ctx.flavor] : []));
    for (var i = 0; i < fl.length; i++) if (fl[i] && !isSafeFlavor(rule, fl[i])) return true;
    return false;
  }
  // City as stored in the delivery_city metaobject.
  function normCity(c) {
    if (!c) return null;
    if (c.__norm) return c;
    var windows = c.slot_windows !== undefined ? c.slot_windows : c.windows;
    if (typeof windows === "string") { try { windows = JSON.parse(windows); } catch (e) { windows = []; } }
    windows = (windows || []).map(function (w) { return { label: w.label, start: num(w.start, 0), end: num(w.end, 0) }; })
      .sort(function (a, b) { return a.start - b.start; });
    return {
      __norm: true,
      handle: c.handle,
      name: c.name,
      windows: windows,
      travel_buffer_hours: num(c.travel_buffer_hours, 0),
      same_day_allowed: c.same_day_allowed === undefined ? true : bool(c.same_day_allowed),
      designer_first_slot_hour: num(c.designer_first_slot_hour, 0),
      active: c.active === undefined ? true : bool(c.active),
      closed_dates: dateList(c.closed_dates)
    };
  }
  // Closed dates arrive as an array, a JSON string, or a comma string; keep valid YYYY-MM-DD only.
  function dateList(v) {
    if (!v) return [];
    if (typeof v === "string") { try { var j = JSON.parse(v); v = Array.isArray(j) ? j : v.split(","); } catch (e) { v = v.split(","); } }
    return (v || []).map(function (x) { return String(x).trim(); }).filter(function (x) { return /^\d{4}-\d{2}-\d{2}$/.test(x); });
  }
  function isClosed(city, ymd) { return !!(city && city.closed_dates && city.closed_dates.indexOf(ymd) !== -1); }
  // First day on or after `ymd` that is not closed (a year of closures at most).
  function nextOpen(city, ymd) {
    for (var i = 0; i < 366 && isClosed(city, ymd); i++) ymd = addDays(ymd, 1);
    return ymd;
  }
  // Is this the rule that gets the "no 9 AM" restriction? Only designer (Hours_6).
  function firstSlotFloor(rule, city) {
    return rule.handle === "designer" ? city.designer_first_slot_hour : 0;
  }
  // Canonical label match: "9AM–11AM" == "9am-11am" (OMS slotKey does the same).
  function slotKey(s) { return String(s || "").toLowerCase().replace(/[‒–—―−]/g, "-").replace(/\s+/g, ""); }

  /* ---------- the two families ---------- */

  // Windows the rule allows on `ymd` for a visitor whose IST clock reads `now`.
  // `closed` = optional array of window labels closed for that date (Step 4).
  function windowsFor(rule, city, ymd, now, opts) {
    opts = opts || {};
    rule = normRule(rule); city = normCity(city);
    if (!rule || !city || !city.active) return [];
    if (isClosed(city, ymd)) return [];
    var closed = (opts.closed || []).map(slotKey);
    var offset = daysBetween(now.ymd, ymd);
    if (offset < 0) return [];
    var earliest = earliestDay(rule, city, now, opts);
    if (daysBetween(earliest, ymd) < 0) return [];

    var out = [];
    var floor = firstSlotFloor(rule, city);
    for (var i = 0; i < city.windows.length; i++) {
      var w = city.windows[i];
      if (closed.indexOf(slotKey(w.label)) !== -1) continue;
      if (offset === 0) {
        // Only the turnaround family can ever be same-day.
        if (rule.family !== "turnaround") continue;
        if (!city.same_day_allowed) continue;
        if (w.start < floor) continue;
        var isLast = i === city.windows.length - 1;
        var need = rule.min_hours + city.travel_buffer_hours - (isLast ? rule.squeeze_hours : 0);
        if (w.start < now.hourDec + need - 1e-9) continue;
      }
      out.push(w.label);
    }
    // Flavour rule: the first same-day window is not offered (see header).
    if (offset === 0 && opts.skipFirstToday && out.length) out.shift();
    return out;
  }

  // First calendar day (YYYY-MM-DD) on which the rule can offer anything.
  // opts.ignoreClosed: answer as if no day were closed (used to tell a real
  // pre-order apart from a cake that only moved past a closed day).
  function earliestDay(rule, city, now, opts) {
    opts = opts || {};
    rule = normRule(rule); city = normCity(city);
    var honourClosed = !opts.ignoreClosed;
    var day;
    if (rule.family === "cutoff") {
      day = addDays(now.ymd, now.hourDec < rule.cutoff_hour ? 1 : 2);
    } else {
      // Turnaround: today if any window still qualifies, else tomorrow.
      var todayOk = false;
      if (city && city.same_day_allowed && !(honourClosed && isClosed(city, now.ymd))) {
        var floor = firstSlotFloor(rule, city);
        for (var i = 0; i < city.windows.length; i++) {
          var w = city.windows[i];
          if (w.start < floor) continue;
          var isLast = i === city.windows.length - 1;
          var need = rule.min_hours + city.travel_buffer_hours - (isLast ? rule.squeeze_hours : 0);
          if (w.start >= now.hourDec + need - 1e-9) { todayOk = true; break; }
        }
      }
      day = addDays(now.ymd, todayOk ? 0 : 1);
      if (rule.min_days > 0) { var md = addDays(now.ymd, rule.min_days); if (daysBetween(day, md) > 0) day = md; }
    }
    // Pre-order floor: never earlier than the product's "pre-order from" date.
    if (opts.preorderFrom && /^\d{4}-\d{2}-\d{2}$/.test(opts.preorderFrom) && daysBetween(day, opts.preorderFrom) > 0) {
      day = opts.preorderFrom;
    }
    // Closed days: move on to the next open one.
    return honourClosed ? nextOpen(city, day) : day;
  }

  // Everything the picker needs to draw itself.
  //   days: [{ ymd, offset, tab: 'today'|'tomorrow'|'earliest', label, windows: [...] }]
  //   calendarMin: first date the free calendar may open at (today+2 or later)
  function plan(rule, city, now, opts) {
    opts = opts || {};
    var nc = normCity(city);
    var e = earliestDay(rule, city, now, opts);
    var offset = daysBetween(now.ymd, e);
    // The flavour rule can empty today (its only window was the first one).
    if (offset === 0 && opts.skipFirstToday && windowsFor(rule, city, now.ymd, now, opts).length === 0) { e = nextOpen(nc, addDays(now.ymd, 1)); offset = daysBetween(now.ymd, e); }
    function laterTab(ymd) {
      var o = daysBetween(now.ymd, ymd);
      return o === 1 ? { ymd: ymd, offset: 1, tab: "tomorrow", label: "Tomorrow", windows: windowsFor(rule, city, ymd, now, opts) }
        : { ymd: ymd, offset: o, tab: "earliest", label: o >= 7 ? labelShort(ymd) : DOW[dowOf(ymd)], windows: windowsFor(rule, city, ymd, now, opts) };
    }
    var days = [];
    if (offset === 0) {
      days.push({ ymd: now.ymd, offset: 0, tab: "today", label: "Today", windows: windowsFor(rule, city, now.ymd, now, opts) });
      // Tomorrow closed: the next open day takes the second tab, named by weekday.
      days.push(laterTab(nextOpen(nc, addDays(now.ymd, 1))));
    } else {
      days.push(laterTab(e));
    }
    var calMin = nextOpen(nc, addDays(now.ymd, Math.max(2, offset)));
    // A cake pushed out only by a closed day is not a pre-order.
    var openOffset = daysBetween(now.ymd, earliestDay(rule, city, now, { preorderFrom: opts.preorderFrom, ignoreClosed: true }));
    return { earliest: e, earliestLong: labelLong(e), days: days, calendarMin: calMin, preorderMode: offset >= 2 && openOffset >= 2 };
  }

  // Is a stored choice (date + window label) still offerable right now?
  function isOfferable(rule, city, ymd, slotLabel, now, opts) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || "")) return false;
    var ws = windowsFor(rule, city, ymd, now, opts).map(slotKey);
    return ws.indexOf(slotKey(slotLabel)) !== -1;
  }

  /* ---------- clock helpers ---------- */

  // Build `now` from an epoch. `overrideIso` (e.g. "2026-09-05T18:30") is an
  // IST wall-clock override used only for QA.
  function nowFrom(epochMs, overrideIso) {
    if (overrideIso && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(overrideIso)) {
      var p = overrideIso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/).slice(1).map(Number);
      return istParts(Date.UTC(p[0], p[1] - 1, p[2], p[3], p[4]) - IST_OFFSET_MIN * 60000);
    }
    return istParts(epochMs);
  }

  return {
    windowsFor: windowsFor,
    earliestDay: earliestDay,
    plan: plan,
    isOfferable: isOfferable,
    nowFrom: nowFrom,
    istParts: istParts,
    addDays: addDays,
    daysBetween: daysBetween,
    labelLong: labelLong,
    labelShort: labelShort,
    slotKey: slotKey,
    normRule: normRule,
    normCity: normCity,
    isSafeFlavor: isSafeFlavor,
    flavorRuleActive: flavorRuleActive
  };
});